import React, { useEffect, useState } from "react";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../../../components/footer";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
  useMediaQuery,
} from "@mui/material";
import ListingDetails from "../../../components/ListingDetails";
import SnackAlert from "../../../components/SnackAlert";
import SwiperImages from "../../../components/SwiperImages";
import GlobalHeader from "../../../components/header";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import PriceCheckOutlinedIcon from "@mui/icons-material/PriceCheckOutlined";
import { useTheme } from "@emotion/react";
import WorkIcon from "@mui/icons-material/Work";
import { PanoramaVerticalSelect } from "@mui/icons-material";

function ActionButtons() {
  const params = useParams();
  return (
    <Stack
      component="section"
      flexDirection="row"
      gap={2}
      sx={{
        width: { xs: "85%", md: "100%" },
        marginInline: "auto",
        justifyContent: "right",
      }}
    >
      <Button
        variant="contained"
        sx={{
          alignItems: "center",
          gap: 1,
          width: {
            xs: "100%",
            md: "fit-content",
          },
          borderRadius: 2,
          color: "white",
          px: { xs: 0, md: 5 },
          "&:hover": {
            backgroundColor: "primary.300",
          },
        }}
        LinkComponent={Link}
        to={`/invest/${params.id}`}
      >
        <WorkIcon />
        INVERTIR
      </Button>
    </Stack>
  );
}

function TableData({ data }) {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#0B2A41",
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: "normal",
    },
  }));
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  return (
    <>
      <Grid container spacing={2}>
        {(data || []).map((elementTable, index) => {
          return (
            <Grid item xs={12} md={6} key={index}>
              <TableContainer component={Paper} key={index} sx={{ mb: 2 }}>
                <Table aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      {(elementTable?.header || []).map((headerEl, index) => {
                        return (
                          <StyledTableCell
                            key={index}
                            width={index === 0 ? "50%" : "25%"}
                            sx={{ textAlign: index !== 0 ? "right" : "left" }}
                          >
                            {headerEl}
                          </StyledTableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {elementTable.body.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="left">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.col1}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.col2}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

const DesktopVersion = ({ detailData, tableInfo }) => {
  return (
    <>
      <Stack component="section" mb={4} mt={{ xs: 3, lg: 6 }}>
        <Typography
          component="h3"
          variant="h5"
          noWrap
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          ¿Qué es {detailData.name}?
        </Typography>
        <Typography mt={2} fontWeight="normal">
          {detailData.information}
        </Typography>
      </Stack>
      <Stack mb={4}>
        <SwiperImages data={detailData.gallery} slides={3} />
      </Stack>
      <Stack mb={4}>
        <TableData data={tableInfo} />
      </Stack>
      {detailData.verified === "approved" ? <ActionButtons /> : null}
    </>
  );
};

const MobileVersion = ({ detailData, tableInfo }) => {
  const [selectedButton, setSelectedButton] = useState("information");

  return (
    <>
      <Stack
        sx={{
          backgroundColor: "primary.300",
          borderRadius: 2,
          mt: 4,
          mb: 4,
        }}
      >
        <Grid container>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("information")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "information" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Información
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("gallery")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "gallery" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Galería
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={() => setSelectedButton("metrics")}
              sx={{
                borderRadius: 2,
                p: 2,
                color: "white",
                backgroundColor:
                  selectedButton === "metrics" ? "primary.lighter" : "none",
              }}
              fullWidth
            >
              Métricas
            </Button>
          </Grid>
        </Grid>
      </Stack>
      {selectedButton === "information" ? (
        <Stack component="section" mb={3}>
          <Typography component="h3" variant="h5">
            ¿Qué es {detailData.name}?
          </Typography>
          <Typography mt={2} fontWeight="normal">
            {detailData.information}
          </Typography>
        </Stack>
      ) : selectedButton === "gallery" ? (
        <Stack mb={3}>
          <SwiperImages data={detailData.gallery} slides={1} />
        </Stack>
      ) : (
        <Stack mb={3}>
          <TableData data={tableInfo} />
        </Stack>
      )}
      {detailData.verified === "approved" ? <ActionButtons /> : null}
    </>
  );
};

const RenderedSection = ({ detailData, listData, tableInfo }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Stack>
      <Box sx={{ width: "100%", height: { xs: 320, md: 400 } }}>
        <img
          src={detailData.photo_profile[0].url}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
      <Stack px={{ xs: 2, md: 4 }}>
        <Stack component="section" pt={5}>
          <Typography
            component="h2"
            variant="h5"
            mb={2}
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {detailData.name}
          </Typography>
          <Typography
            component="span"
            noWrap
            sx={{
              backgroundColor: "secondary.typePropertySpan",
              width: "fit-content",
              px: 2,
              py: 0.5,
              borderRadius: 3,
              mb: 5,
              fontWeight: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {detailData.name_business}
          </Typography>
          {/* <ListingDetails data={listData} /> */}
          <Stack>
            <Grid container spacing={2}>
              {(listData || []).map((detailData, index) => {
                return (
                  <Grid item xs={6} md={4} key={index}>
                    <Box
                      sx={{
                        boxShadow: "2px 3px 5px #7d7d7d24",
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        gap={2}
                        mb={1}
                      >
                        <detailData.icon
                          sx={{
                            color: "gray",
                            backgroundColor: "#F3F4F6",
                            p: 1,
                            borderRadius: "50%",
                          }}
                        />
                        <Typography
                          component="h3"
                          variant="subtitle1"
                          fontWeight={400}
                          color="#303030ed"
                        >
                          {detailData.title}
                        </Typography>
                      </Box>
                      <Typography component="span" fontFamily={"sans-serif"}>
                        {detailData.value}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Stack>
        <Stack mt={6}>
          <Typography component="h3" variant="h5" mb={2}>
            Progreso del proyecto
          </Typography>
          <Stack flexDirection="row" justifyContent="space-between" mb={1}>
            <Stack flexDirection="row" justifyContent="start" gap={1}>
              <Typography
                fontWeight="normal"
                fontSize={{ xs: "small", md: "large" }}
              >
                Invertidos:
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={{ xs: "small", md: "large" }}
              >
                {parseInt(detailData.invested).toLocaleString("es-CO")}€
              </Typography>
            </Stack>
            <Stack flexDirection="row" justifyContent="end" gap={1}>
              <Typography
                fontWeight="normal"
                fontSize={{ xs: "small", md: "large" }}
              >
                Inversión:
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={{ xs: "small", md: "large" }}
              >
                {parseInt(detailData.objective).toLocaleString("es-CO")}€
              </Typography>
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={
              parseInt(detailData.completed) > 100
                ? 100
                : parseInt(detailData.completed)
            }
            sx={{ height: "10px", fill: "primary.300", borderRadius: 1 }}
          />
        </Stack>
        {isSmallScreen ? (
          <MobileVersion detailData={detailData} tableInfo={tableInfo} />
        ) : (
          <DesktopVersion detailData={detailData} tableInfo={tableInfo} />
        )}
      </Stack>
    </Stack>
  );
};

function DetailsProject() {
  // GET DETAILS REQUEST

  const navigate = useNavigate();

  const GetDetails = async () => {
    setOpenBackDrop(true);
    loadApi(`project/${params.id}`, true, "get")
      .then((response) => {
        const dataMapping = {
          "Inversión Minima":
            parseInt(response.data.project.min_investment).toLocaleString(
              "es-ES"
            ) + "Є",
          Invertidos: parseInt(response.data.project.invested).toLocaleString(
            "es-ES"
          ),
          Rentabilidad:
            parseInt(response.data.project.esti_profitability).toLocaleString(
              "es-ES"
            ) + "%",
          Completado: response.data.project.completed + "%",
          Inversores: response.data.project.cant_investors,
          "Días Restantes": response.data.diffDays,
          Inversión:
            parseInt(response.data.project.objective).toLocaleString("es-CO") +
            "Є",
        };
        const updatedListData = listData.map((item) => ({
          ...item,
          value: dataMapping[item.title],
        }));

        const updatedTableInfo = [
          {
            header: ["Inversión", "MIN", "MAX"],
            body: [
              {
                name: "Capital Ofrecido",
                col1: parseInt(
                  response.data.project.DetailProject.min_offered_capital
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_offered_capital
                ).toLocaleString("es-CO"),
              },
              {
                name: "Participaciones a emitir",
                col1: response.data.project.DetailProject.min_stake,
                col2: response.data.project.DetailProject.max_stake,
              },
              {
                name: "Valor por participación",
                col1: parseInt(
                  response.data.project.min_investment
                ).toLocaleString("es-CO"),
                col2: parseInt(response.data.project.objective).toLocaleString(
                  "es-CO"
                ),
              },
            ],
          },
          {
            header: ["Rentabilidad Aproximada", "", ""],
            body: [
              {
                name: "Retorno de inversión",
                col2: parseInt(
                  response.data.project.DetailProject.max_return_investment
                ).toLocaleString("es-CO"),
              },
              {
                name: "Rentabilidad recursos propios",
                col2: parseInt(response.data.project.objective).toLocaleString(
                  "es-CO"
                ),
              },
            ],
          },
          {
            header: ["Plan de negocios", "MIN", "MAX"],
            body: [
              {
                name: "Marketing",
                col1: parseInt(
                  response.data.project.DetailProject.min_marketing
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_marketing
                ).toLocaleString("es-CO"),
              },
              {
                name: "Construcción",
                col1: parseInt(
                  response.data.project.DetailProject.min_construction
                ).toLocaleString("es-CO"),
                col2: parseInt(
                  response.data.project.DetailProject.max_construction
                ).toLocaleString("es-CO"),
              },
            ],
          },
          {
            header: ["Garantías", "", "Valor"],
            body: [
              {
                name: "Inmobiliaria",
                col1: "",
                col2: response.data.project.DetailProject.warranty_economica
                  ? parseInt(
                      response.data.project.DetailProject.warranty_economica
                    ).toLocaleString("es-CO")
                  : "No disponible",
              },
              {
                name: "Económica",
                col1: "",
                col2: response.data.project.DetailProject.warranty_inmobiliaria
                  ? parseInt(
                      response.data.project.DetailProject.warranty_inmobiliaria
                    ).toLocaleString("es-CO")
                  : "No disponible",
              },
            ],
          },
        ];

        setTableInfo(updatedTableInfo);

        setListData(updatedListData);
        setDetailData(response.data.project);
      })
      .catch((e) => {
        navigate("/projects");
      })
      .finally(() => setOpenBackDrop(false));
  };

  // DATA
  const [detailData, setDetailData] = useState(false);
  const [listData, setListData] = useState([
    {
      title: "Inversión Minima",
      icon: LocalActivityIcon,
      value: "",
    },
    {
      title: "Invertidos",
      icon: LocalAtmIcon,
      value: "",
    },
    {
      title: "Rentabilidad",
      icon: AutoGraphIcon,
      value: "",
    },
    {
      title: "Inversores",
      icon: PersonOutlineOutlinedIcon,
      value: "",
    },
    {
      title: "Días Restantes",
      icon: WatchLaterOutlinedIcon,
      value: "",
    },
    {
      title: "Inversión",
      icon: StarOutlinedIcon,
      value: "",
    },
  ]);
  const [tableInfo, setTableInfo] = useState(false);

  // UTILS
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();

  const params = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    GetDetails();
  }, []);

  return (
    <>
      <GlobalHeader />
      <Stack
        component="main"
        sx={{
          minHeight: "90vh",
          width: { xs: "100vw", sm: "90vw", md: "85vw", lg: "65vw" },
          marginInline: "auto",
          pb: 4,
          backgroundColor: "white",
        }}
      >
        {detailData && (
          <>
            {/* IF THE ARRAY IS EMPTY THIS WILL BE SHOW IT */}
            {detailData.error && (
              <Stack sx={{ mt: 10 }}>
                <Typography textAlign="center" variant="h5">
                  Lo sentimos, datos no encontrados
                </Typography>
              </Stack>
            )}
            {/* IN CASE THAT THE ERROR KEY DOESNT EXIST, THE DATA IS RENDERED */}
            {!detailData.error && (
              <RenderedSection
                detailData={detailData}
                listData={listData}
                tableInfo={tableInfo}
              />
            )}
          </>
        )}
      </Stack>
      <Footer />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

export default DetailsProject;

